import {
  AppstoreOutlined,
  DashboardOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ProjectOutlined,
  SettingOutlined
} from '@ant-design/icons'
import { Button, Drawer, Grid, Typography } from 'antd'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { Footer } from '@/components/footer'
import { ThemeToggle } from '@/components/theme-toggle'
import { UserMenu } from '@/components/user-menu'
const { useBreakpoint } = Grid
const { Text } = Typography

interface NavItemProps {
  active: boolean
  icon: ReactNode
  label: string
  hint?: string
  onClick: () => void
  collapsed?: boolean
}

function NavItem({ active, icon, label, hint, onClick, collapsed }: NavItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`app-nav-item${active ? ' is-active' : ''}${collapsed ? ' is-collapsed' : ''}`}
    >
      <span className="app-nav-item__icon">{icon}</span>
      <span className="app-nav-item__content">
        <span className="app-nav-item__label">{label}</span>
        {!collapsed && hint ? <span className="app-nav-item__hint">{hint}</span> : null}
      </span>
    </button>
  )
}

export function AppLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const screens = useBreakpoint()
  const isDesktop = Boolean(screens.lg)
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const currentPath = location.pathname

  const goProjects = () => navigate('/projects')
  const goDashboard = () => navigate('/dashboard')
  const goSettings = () => navigate('/settings')

  useEffect(() => {
    setMobileOpen(false)
  }, [currentPath])

  const currentSection = useMemo(() => {
    if (currentPath === '/dashboard') return '平台看板'
    if (currentPath === '/settings') return '系统设置'
    if (currentPath === '/projects/create') return '新建项目'
    if (currentPath.startsWith('/projects/')) return '项目详情'
    return '项目货架'
  }, [currentPath])

  const navItems = [
    {
      key: 'projects',
      active: currentPath === '/projects' || currentPath.startsWith('/projects/'),
      icon: <ProjectOutlined />,
      label: '项目货架',
      hint: '项目列表与详情',
      onClick: goProjects
    },
    {
      key: 'dashboard',
      active: currentPath === '/dashboard',
      icon: <DashboardOutlined />,
      label: '统计看板',
      hint: '跨项目运营概览',
      onClick: goDashboard
    },
    {
      key: 'settings',
      active: currentPath === '/settings',
      icon: <SettingOutlined />,
      label: '系统设置',
      hint: '账户与界面偏好',
      onClick: goSettings
    }
  ]

  const siderContent = (
    <div className={`app-sider${collapsed && isDesktop ? ' is-collapsed' : ''}`}>
      <div className="app-sider__section-label">导航</div>
      <nav className="app-nav-list" aria-label="主导航">
        {navItems.map((item) => (
          <NavItem
            key={item.key}
            active={item.active}
            icon={item.icon}
            label={item.label}
            hint={item.hint}
            onClick={item.onClick}
            collapsed={collapsed && isDesktop}
          />
        ))}
      </nav>

      <div className="app-sider__spacer" />

      <div className="app-sider__tip">
        <div className="app-sider__tip-title">
          <AppstoreOutlined />
          <span>{collapsed && isDesktop ? '提示' : '操作提示'}</span>
        </div>
        {!collapsed || !isDesktop ? (
          <p>常用发卡、换机、吊销等操作统一收敛在项目详情页，避免跨层级跳转打断上下文。</p>
        ) : null}
      </div>
    </div>
  )

  return (
    <div className="app-shell">
      <header className="app-topbar">
        <div className="app-topbar__left">
          {!isDesktop ? (
            <Button
              type="text"
              icon={<MenuUnfoldOutlined />}
              onClick={() => setMobileOpen(true)}
              aria-label="打开导航菜单"
              className="app-topbar__menu-trigger"
            />
          ) : null}

          <button type="button" className="app-brand" onClick={goProjects}>
            <span className="app-brand__mark">L</span>
            <span className="app-brand__text">
              <span className="app-brand__title">License Platform</span>
              <span className="app-brand__subtitle">Admin Console</span>
            </span>
          </button>
        </div>

        <div className="app-topbar__center">
          <Text className="app-topbar__section-label" type="secondary">
            当前区域
          </Text>
          <span className="app-topbar__section-name">{currentSection}</span>
        </div>

        <div className="app-topbar__actions">
          {isDesktop ? (
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed((value) => !value)}
              aria-label={collapsed ? '展开侧边栏' : '收起侧边栏'}
              className="app-topbar__collapse-trigger"
            />
          ) : null}
          <ThemeToggle />
          <UserMenu />
        </div>
      </header>

      <div className="app-shell__body">
        {isDesktop ? (
          <aside className={`app-shell__sider${collapsed ? ' is-collapsed' : ''}`}>
            {siderContent}
          </aside>
        ) : null}

        {!isDesktop ? (
          <Drawer
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            placement="left"
            closable={false}
            width={288}
            styles={{
              body: { padding: 0, background: 'var(--app-bg-sider)' }
            }}
          >
            {siderContent}
          </Drawer>
        ) : null}

        <div className="app-shell__main">
          <main className="app-shell__content">
            <div className="app-shell__content-inner">
              <Outlet />
            </div>
          </main>

          <Footer />
        </div>
      </div>
    </div>
  )
}

/** 重定向到 /projects */
export function RedirectToProjects() {
  return <Navigate to="/projects" replace />
}
